import {
  FeatureTreeNodeKind,
  type FeatureTreeNodeMeta,
} from './feature-tree-types';

/**
 * 视口装配组件右键菜单目标解析输入。
 */
export interface ViewportAssemblyContextMenuTargetInput {
  /** 当前视口命中的运行时对象 ID 列表。 */
  readonly objectIds: readonly string[];
  /** cad-core 从命中对象解析出的装配实例节点 ID 列表。 */
  readonly assemblyNodeIds: readonly string[];
  /** 当前设计树节点元数据映射。 */
  readonly featureTreeNodeMap: ReadonlyMap<string, FeatureTreeNodeMeta>;
}

/**
 * 视口装配组件右键菜单目标。
 */
export interface ViewportAssemblyContextMenuTarget {
  /** 可交给设计树 action 层执行命令的节点 key。 */
  readonly treeKey: string;
  /** 命中的设计树节点元数据。 */
  readonly nodeMeta: FeatureTreeNodeMeta;
}

/**
 * 归一化候选 ID，避免空白字符串参与匹配。
 *
 * @param value 原始 ID。
 * @returns 去除首尾空白后的 ID；无效时返回空字符串。
 */
function normalizeTargetId(value: string | null | undefined): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 创建非空 ID 集合。
 *
 * @param values 原始 ID 列表。
 * @returns 已去重的非空 ID 集合。
 */
function createTargetIdSet(values: readonly string[]): ReadonlySet<string> {
  return new Set(values.map(normalizeTargetId).filter((value) => value.length > 0));
}

/**
 * 判断候选 ID 是否命中集合。
 *
 * @param values 候选 ID 列表。
 * @param acceptedValues 命中集合。
 * @returns 存在命中值时返回 `true`。
 */
function hasAnyMatchedId(
  values: readonly (string | null | undefined)[],
  acceptedValues: ReadonlySet<string>,
): boolean {
  return values.some((value) => {
    const normalizedValue = normalizeTargetId(value);
    return normalizedValue.length > 0 && acceptedValues.has(normalizedValue);
  });
}

/**
 * 判断节点是否代表装配组件或子装配体。
 *
 * @param nodeMeta 设计树节点元数据。
 * @returns 节点是否可作为装配组件菜单目标。
 */
function isAssemblyComponentNode(nodeMeta: FeatureTreeNodeMeta): boolean {
  return nodeMeta.kind === FeatureTreeNodeKind.GlbNode
    && (
      nodeMeta.glbNodeRole === 'part'
      || nodeMeta.glbNodeRole === 'assembly'
      || nodeMeta.glbNodeRole === 'component'
    );
}

/**
 * 判断高亮目标是否命中视口对象。
 *
 * @param nodeMeta 设计树节点元数据。
 * @param objectIds 视口对象 ID 集合。
 * @returns 高亮目标命中时返回 `true`。
 */
function hasMatchedHighlightTarget(
  nodeMeta: FeatureTreeNodeMeta,
  objectIds: ReadonlySet<string>,
): boolean {
  return nodeMeta.highlightTargets?.some((target) => objectIds.has(target.objectId)) === true;
}

/**
 * 计算设计树节点与视口右键目标的匹配分。
 *
 * 设计说明：
 * 1. SOLIDWORKS 中右键零部件面时菜单语境属于该零部件，而不是面代理；
 * 2. 因此装配实例 nodeId 的优先级高于运行时 Object3D UUID；
 * 3. 高亮目标只作为旧数据或缺失装配 ID 时的兜底，避免把 Body 节点误当组件菜单目标。
 *
 * @param nodeMeta 设计树节点元数据。
 * @param objectIds 视口对象 ID 集合。
 * @param assemblyNodeIds 装配节点 ID 集合。
 * @returns 匹配分；0 表示不匹配。
 */
function resolveMatchScore(
  nodeMeta: FeatureTreeNodeMeta,
  objectIds: ReadonlySet<string>,
  assemblyNodeIds: ReadonlySet<string>,
): number {
  const componentCandidate = isAssemblyComponentNode(nodeMeta);
  const assemblyNodeMatched = hasAnyMatchedId(
    [
      nodeMeta.assemblyNodeId,
      nodeMeta.glbSourceObjectId,
      nodeMeta.objectId,
      nodeMeta.glbPath,
    ],
    assemblyNodeIds,
  );
  const directObjectMatched = hasAnyMatchedId(
    [
      nodeMeta.objectId,
      nodeMeta.glbSourceObjectId,
      ...(nodeMeta.resultObjectIds ?? []),
    ],
    objectIds,
  );
  const highlightMatched = hasMatchedHighlightTarget(nodeMeta, objectIds);

  if (nodeMeta.canFixOrFloat === true && assemblyNodeMatched) {
    return 130;
  }
  if (componentCandidate && assemblyNodeMatched) {
    return 120;
  }
  if (nodeMeta.canFixOrFloat === true && directObjectMatched) {
    return 110;
  }
  if (componentCandidate && directObjectMatched) {
    return 100;
  }
  if (nodeMeta.canFixOrFloat === true && highlightMatched) {
    return 90;
  }
  if (componentCandidate && highlightMatched) {
    return 80;
  }
  if (assemblyNodeMatched) {
    return 60;
  }
  if (directObjectMatched) {
    return 50;
  }
  if (highlightMatched) {
    return 40;
  }
  return 0;
}

/**
 * 解析视口右键命中的装配组件设计树目标。
 *
 * @param input 视口命中上下文。
 * @returns 命中的设计树目标；无法解析时返回 `null`。
 */
export function resolveViewportAssemblyContextMenuTarget(
  input: ViewportAssemblyContextMenuTargetInput,
): ViewportAssemblyContextMenuTarget | null {
  const objectIds = createTargetIdSet(input.objectIds);
  const assemblyNodeIds = createTargetIdSet(input.assemblyNodeIds);
  let bestMatch: { treeKey: string; nodeMeta: FeatureTreeNodeMeta; score: number } | null = null;

  input.featureTreeNodeMap.forEach((nodeMeta, treeKey) => {
    const score = resolveMatchScore(nodeMeta, objectIds, assemblyNodeIds);
    if (score <= 0) {
      return;
    }
    if (!bestMatch || score > bestMatch.score) {
      bestMatch = { treeKey, nodeMeta, score };
    }
  });

  if (!bestMatch) {
    return null;
  }

  return {
    treeKey: bestMatch.treeKey,
    nodeMeta: bestMatch.nodeMeta,
  };
}
